'use client';

import * as React from 'react';


type ToastActionElement = React.ReactElement;

type ToastProps = {
    id: string;
    title?: string;
    description?: string;
    action?: ToastActionElement;
    variant?: 'default' | 'destructive';
};

const TOAST_LIMIT        = 3;
const TOAST_REMOVE_DELAY = 4000;

type State = { toasts: ToastProps[] };

type Action =
    | { type: 'ADD_TOAST'; toast: ToastProps }
    | { type: 'UPDATE_TOAST'; toast: Partial<ToastProps> & { id: string } }
    | { type: 'DISMISS_TOAST'; toastId?: string }
    | { type: 'REMOVE_TOAST'; toastId?: string };

let count = 0;

function genId () {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const listeners: Array<(state: State) => void> = [];
let memoryState: State                         = { toasts: [] };

function reducer (state: State, action: Action): State {
    switch (action.type) {
        case 'ADD_TOAST': {
            const toasts = [ action.toast, ...state.toasts ].slice(0, TOAST_LIMIT);
            return { ...state, toasts };
        }
        case 'UPDATE_TOAST': {
            return {
                ...state,
                toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
            };
        }
        case 'DISMISS_TOAST': {
            const { toastId } = action;

            if (toastId) {
                const timeout = toastTimeouts.get(toastId);
                if (timeout) clearTimeout(timeout);
                toastTimeouts.set(
                    toastId,
                    setTimeout(() => dispatch({ type: 'REMOVE_TOAST', toastId }), 300),
                );
            } else {
                state.toasts.forEach((t) => {
                    const timeout = toastTimeouts.get(t.id);
                    if (timeout) clearTimeout(timeout);
                    toastTimeouts.set(
                        t.id,
                        setTimeout(() => dispatch({ type: 'REMOVE_TOAST', toastId: t.id }), 300),
                    );
                });
            }

            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    toastId === undefined || t.id === toastId ? { ...t } : t,
                ),
            };
        }
        case 'REMOVE_TOAST': {
            if (action.toastId === undefined) return { ...state, toasts: [] };
            return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) };
        }
        default:
            return state;
    }
}

function dispatch (action: Action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((l) => l(memoryState));
}

type ToastInput = Omit<ToastProps, 'id'>;

export function toast (input: ToastInput) {
    const id = genId();

    const t: ToastProps = {
        id,
        variant    : input.variant ?? 'default',
        title      : input.title,
        description: input.description,
        action     : input.action,
    };

    dispatch({ type: 'ADD_TOAST', toast: t });

    const timeout = setTimeout(() => dispatch({ type: 'DISMISS_TOAST', toastId: id }), TOAST_REMOVE_DELAY);
    toastTimeouts.set(id, timeout);

    return {
        id,
        dismiss: () => dispatch({ type: 'DISMISS_TOAST', toastId: id }),
        update : (patch: Partial<ToastProps>) => dispatch({ type: 'UPDATE_TOAST', toast: { ...patch, id } }),
    };
}

export function useToast () {
    const [ state, setState ] = React.useState<State>(memoryState);

    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1) listeners.splice(index, 1);
        };
    }, []);

    return {
        toast,
        dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
        toasts : state.toasts,
    };
}
